import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import { useForm, Controller } from 'react-hook-form';
import ReactJson from 'react-json-view';

import { appActions, rtApiDemoActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { Col, Div, Icon, Input, Row, Text, Autocomplete } from '@pinpoint/design-kit';
import { Card, Switch, Message, Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { ModelProp } from 'src/store/typings';

const defaultValues = {
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
};
const menuItems = CONSTANTS.GENERAL.FORM.US_STATES;

function RtApiDemo({
  app: {
    profile: { tenant },
  },
  history,
  match: {
    params: { configId },
  },
  rtApiDemo: { config, demoResponse, fetchConfigStatus, fetchConfigError, demoResponseStatus, demoResponseError },
  enqueueNotification,
  fetchConfig,
  submitDemo,
  handleUnmount,
  unsetDemoResponse,
}) {
  const [ready, setReady] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const modelUrl = useMemo(() => {
    if (config) {
      const modelId = config?.config_metadata?.model_id;
      const modelType = config?.config_metadata?.data_model_type;
      if (modelId && modelType) {
        const modelClass = modelType.includes('ACT_') ? 'actuarial' : 'classifier';
        return modelId ? `${window.location.origin}/models/${modelClass}/${modelId}/lift-charts?ref=rtapi` : null;
      }
    }
    return null;
  }, [config]);
  const {
    formState: { isDirty },
    handleSubmit,
    control,
    reset,
    setValue,
  } = useForm({ defaultValues });

  // eslint-disable-next-line no-console
  const onSubmit = (data) => submitDemo(data);
  const handleReset = useCallback(() => {
    reset(defaultValues);
    unsetDemoResponse();
  }, [reset, unsetDemoResponse]);
  const getData = useCallback(() => fetchConfig(configId), [configId, fetchConfig]);
  const toggleStateDropdown = useCallback(
    () => setStateDropdownOpen(!stateDropdownOpen),
    [stateDropdownOpen, setStateDropdownOpen]
  );
  const handleSelection = useCallback(
    (index) => {
      const nextUSState = menuItems[index];
      setValue('state', nextUSState, { shouldDirty: true });
    },
    [setValue]
  );

  useEffect(() => {
    if (tenant && (config === null || get(config, 'id') !== configId)) {
      getData();
    }
  }, [tenant, config, configId, getData]);

  useEffect(() => {
    if (fetchConfigStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Unable to retrieve API demo config\n${fetchConfigError}`);
    }
    if (demoResponseStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Unable to submit your request\n${demoResponseError}`);
    }
  }, [fetchConfigStatus, demoResponseStatus, enqueueNotification, fetchConfigError, demoResponseError]);

  useEffect(() => {
    if (tenant && config && get(config, 'id') && get(config, 'config_metadata.model')) {
      setReady(true);
      handleReset();
    }
  }, [setReady, config, tenant, handleReset]);

  return (
    <Div d="flex" flexDir="column" h="100%" minH="100%" w="100%" minW="100%" data-debug="content" p={{ x: '-1.15rem' }}>
      <Row data-debug="nav-row" m={{ x: '1.5rem', t: '1.5rem' }}>
        <Col justify="left" align="center" gutterWidth="0" d="flex" size="12">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push('/rt');
              handleUnmount();
            }}
          >
            <Icon name="LongLeft" color="white" m={{ r: '0.5rem' }} size="1rem" />
            BACK
          </Button>
        </Col>
      </Row>
      <Row m={{ x: '1.5rem', t: '1.5rem' }} flexDir="row" h="55vh">
        <Col size="12" justify="left" gutterWidth="0" d="flex" alignSelf="stretch">
          <Card
            title={
              ready && (
                <Div d="flex">
                  <Text textWeight="600">Demo:</Text>
                  <Text>&ensp;{get(config, 'config_metadata.model.name')}</Text>
                </Div>
              )
            }
            iconProps={{
              name: 'PresentationScreen',
              color: 'white',
              size: '3.125rem',
              viewBox: '0 0 18 16',
            }}
            titleProps={{
              bg: 'marigold',
              h: '5.5rem',
              p: { l: '2.5rem', r: '2.625rem' },
              rounded: { tr: 'md', tl: 'md' },
            }}
            titleTextProps={{
              textColor: 'white',
              textSize: 'title',
              lineHeight: 'title',
            }}
            cardProps={{
              p: '0',
              textSize: 'paragraph',
              style: {
                lineHeight: '1.5rem',
              },
              rounded: 'md',
              textColor: 'shade66',
              d: 'flex',
              flexDir: 'column',
            }}
          >
            <Switch status={fetchConfigStatus}>
              <Message bg="white" value={CONSTANTS.API.STARTED} />
              <Message type="error" bg="white" value={CONSTANTS.API.FAILED} />
              <Col value={CONSTANTS.API.SUCCEEDED} size="12" flexGrow="1">
                <Row p={{ t: '3.375rem', l: '3rem', r: '3rem' }}>
                  <Col size="6" data-debug="col-left">
                    <Row p={{ r: '1.5rem', b: '1rem' }}>
                      <Col size={{ xs: '12', md: '2' }} />
                      <Col size={{ xs: '12', md: '10' }} gutterWidth="2rem">
                        <Row>Enter available contact info and press the &quot;PREDICT&quot; button</Row>
                      </Col>
                    </Row>
                    <Row p={{ r: '1.5rem' }}>
                      <Col
                        size={{ xs: '12', md: '2' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                        gutterWidth="0"
                      >
                        First Name
                      </Col>
                      <Col size={{ xs: '12', md: '10' }}>
                        <Controller h="1.875rem" name="firstName" as={Input} control={control} />
                      </Col>
                    </Row>
                    <Row p={{ t: '1rem', r: '1.5rem' }}>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '2' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        Last Name
                      </Col>
                      <Col size={{ xs: '12', md: '10' }}>
                        <Controller h="1.875rem" name="lastName" as={Input} control={control} />
                      </Col>
                    </Row>
                    <Row p={{ t: '1rem', r: '1.5rem' }}>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '2' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        Address
                      </Col>
                      <Col size={{ xs: '12', md: '10' }}>
                        <Controller h="1.875rem" name="address" as={Input} control={control} />
                      </Col>
                    </Row>
                    <Row p={{ t: '1rem', r: '1.5rem' }}>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '2' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        City
                      </Col>
                      <Col size={{ xs: '12', md: '4' }}>
                        <Controller h="1.875rem" name="city" as={Input} control={control} />
                      </Col>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '1' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        State
                      </Col>
                      <Col size={{ xs: '12', md: '2' }}>
                        <Controller
                          name="state"
                          as={Autocomplete}
                          control={control}
                          isOpen={stateDropdownOpen}
                          onSelection={handleSelection}
                          onClick={toggleStateDropdown}
                          menu={CONSTANTS.GENERAL.FORM.US_STATES}
                          DropdownButtonOptions={{
                            h: '1.875rem',
                            w: '2rem',
                            rounded: { tr: 'md', br: 'md' },
                            border: { y: '1px solid', r: '1px solid', l: '0' },
                            borderColor: 'gray500',
                            hoverBg: 'white',
                            bg: 'white',
                            align: 'center',
                            justify: 'center',
                            d: 'inline-block',
                            p: '0',
                            transition: 'all 0.2s ease-in-out',
                          }}
                          inputStyle={{
                            h: 'calc(1.875rem-1px)',
                            textWeight: '500',
                            bg: 'white',
                            hoverBg: 'white',
                            style: {
                              transition: 'all 0.2s ease-in-out',
                              font: 'inherit',
                            },
                          }}
                          DivOptions={{
                            rounded: stateDropdownOpen ? { tl: 'md', bl: '0', br: '0', tr: '0' } : { l: 'md', r: '0' },
                            border: stateDropdownOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid',
                            style: {
                              transition: 'all 0.2s ease-in-out',
                            },
                          }}
                          menuItemOptions={{
                            p: { y: '.25rem', x: '.75rem' },
                            textWeight: '500',
                            bg: 'white',
                            hoverBg: 'shadeF2',
                          }}
                          menuOptions={{
                            border: { b: '1px solid', x: '1px solid', t: '0' },
                            borderColor: 'gray500',
                            rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
                            pos: 'absolute',
                            shadow: 4,
                            minW: 'calc(100% - 2rem)',
                            maxW: 'calc(100% - 2rem)',
                            style: {
                              transition: 'all 0.2s ease-in-out',
                            },
                            maxH: '250px',
                            overflow: 'auto',
                            m: { t: '-2px' },
                          }}
                        />
                      </Col>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '1' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        Zip
                      </Col>
                      <Col size={{ xs: '12', md: '2' }}>
                        <Controller h="1.875rem" name="zip" as={Input} control={control} />
                      </Col>
                    </Row>
                    <Row p={{ t: '1rem', r: '1.5rem' }}>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '2' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        Email
                      </Col>
                      <Col size={{ xs: '12', md: '10' }}>
                        <Controller h="1.875rem" name="email" as={Input} control={control} />
                      </Col>
                    </Row>
                    <Row p={{ t: '1rem', r: '1.5rem' }}>
                      <Col
                        gutterWidth="0"
                        size={{ xs: '12', md: '2' }}
                        d="flex"
                        align="center"
                        minH="1.875rem"
                        justify="flex-end"
                        textSize="input"
                      >
                        Phone
                      </Col>
                      <Col size={{ xs: '12', md: '10' }}>
                        <Controller h="1.875rem" name="phone" as={Input} control={control} />
                      </Col>
                    </Row>
                  </Col>
                  <Col size="6" data-debug="col-right" d="flex" flexDir="column">
                    <Div
                      bg="shadeF2"
                      flexGrow="1"
                      m={{ l: '1.5rem', t: '.5rem' }}
                      rounded="md"
                      border="1px solid"
                      borderColor="shadeD9"
                      textColor="black"
                      textSize="input"
                    >
                      <ReactJson
                        src={demoResponse || {}}
                        sortKeys
                        iconStyle="triangle"
                        indentWidth={2}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        collapseStringsAfterLength={false}
                        collapsed={false}
                        enableClipboard={false}
                        name={null}
                        style={{
                          padding: '.5rem .5rem 0rem .5rem',
                          maxHeight: '9rem',
                          height: 'auto',
                        }}
                      />
                    </Div>
                  </Col>
                </Row>
                <Row p={{ t: '1rem', b: '3rem', l: '3rem' }}>
                  <Col offset="6" size="6" d="flex">
                    <Button buttonStyle="primary" onClick={handleReset} disabled={!isDirty}>
                      <Icon name="Reset" color="white" m={{ r: '0.5rem' }} size="1rem" viewBox="0 0 72 72" />
                      RESET
                    </Button>
                    <Button buttonStyle="primary" disabled={!isDirty} onClick={handleSubmit(onSubmit)}>
                      <Icon name="ThinkalikeModel" color="white" m={{ r: '0.5rem' }} size="1rem" />
                      PREDICT
                    </Button>
                    {!config?.config_metadata?.model?.modelData?.metadataPayload?.shared && (
                      <Div data-test="chart-btn-div">
                        <Button
                          buttonStyle="primary"
                          disabled={!modelUrl || modelUrl.includes('null')}
                          onClick={() => {
                            window.open(modelUrl, '_blank');
                          }}
                        >
                          <Icon
                            name="BarChartEnclosed"
                            color="white"
                            viewBox="0 0 16 17"
                            m={{ r: '0.5rem' }}
                            size="1rem"
                          />
                          CHART
                          <Icon name="NewWindow2" color="white" m={{ l: '0.5rem' }} size=".75rem" viewBox="0 0 12 12" />
                        </Button>
                      </Div>
                    )}
                  </Col>
                </Row>
              </Col>
              <Message default value={null} type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />
            </Switch>
          </Card>
        </Col>
      </Row>
    </Div>
  );
}

const mapStateToProps = ({ app, rtApiDemo }) => ({ app, rtApiDemo });

const mapDispatchToProps = (dispatch) => ({
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
  fetchConfig: (id) => dispatch(rtApiDemoActions.fetchConfig(id)),
  submitDemo: (payload) => dispatch(rtApiDemoActions.submitDemoRequest(payload)),
  handleUnmount: () => dispatch(rtApiDemoActions.unset('config')),
  unsetDemoResponse: () => {
    dispatch(rtApiDemoActions.unset('demoResponse'));
    dispatch(rtApiDemoActions.unset('demoResponseStatus'));
  },
});

RtApiDemo.propTypes = {
  app: PropTypes.shape({
    profile: PropTypes.shape({
      originalTenant: PropTypes.string.isRequired,
      tenant: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  }).isRequired,
  match: PropTypes.shape({ params: PropTypes.shape({ configId: PropTypes.string.isRequired }).isRequired }).isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  rtApiDemo: PropTypes.shape({
    config: PropTypes.shape({
      id: PropTypes.string.isRequired,
      config_metadata: PropTypes.shape({
        model: ModelProp,
        model_id: PropTypes.number.isRequired,
        data_model_type: PropTypes.string.isRequired,
      }).isRequired,
    }),
    fetchConfigStatus: PropTypes.string,
    fetchConfigError: PropTypes.string,
    demoResponse: PropTypes.shape({}),
    demoResponseStatus: PropTypes.string,
    demoResponseError: PropTypes.string,
  }).isRequired,
  fetchConfig: PropTypes.func.isRequired,
  submitDemo: PropTypes.func.isRequired,
  handleUnmount: PropTypes.func.isRequired,
  unsetDemoResponse: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(RtApiDemo));
